import type { ApiRegisterUserJourneyEventParams } from '~/handlers/ports/api-register-user-journey-event.port';
import {
  registerUserJourneyEventHandler,
  UserJourneyEvent,
} from '~/handlers/register-user-journey-event';
import { useMarketingTrackingStore } from '~/stores/apostatudo/marketing-tracking';
import { useDeviceStore } from '~/stores/apostatudo/device';
import { useUserJourneyStore } from '~/stores/apostatudo/user-journey';
import { cleanObject } from '~/utils/apostatudo/clean-object';
import isDeepEqual from '~/utils/apostatudo/is-deep-equal';

type UserJourneyPayload = Omit<ApiRegisterUserJourneyEventParams, 'event'>;

export const useUserJourney = () => {
  const journeyStore = useUserJourneyStore();
  const { fingerprint } = storeToRefs(useDeviceStore());
  const marketingStore = useMarketingTrackingStore();
  const {
    utm: marketingUtm,
    google: marketingGoogle,
    facebook: marketingFacebook,
    tiktok: marketingTiktok,
    apt: marketingApt,
  } = storeToRefs(marketingStore);

  const { userId } = useUserData();
  // Detecta bots para evitar delay desnecessário que afeta LCP
  const { isBot } = usePerformanceBotBypass();
  const { isNativeApp } = useAppTrackingPolicy();

  const buildPayload = (raw: UserJourneyPayload): UserJourneyPayload => {
    const payload: UserJourneyPayload = {};

    if (raw.user?.id) payload.user = { id: raw.user.id };
    if (raw.device?.fingerprint)
      payload.device = { fingerprint: raw.device.fingerprint };
    if (typeof raw.deposit_id === 'number') payload.deposit_id = raw.deposit_id;

    const utm = cleanObject(raw.utm);

    if (utm) payload.utm = utm;

    const google = cleanObject(raw.google);

    if (google) payload.google = google;

    const facebook = cleanObject(raw.facebook);

    if (facebook) payload.facebook = facebook;

    const tiktok = cleanObject(raw.tiktok);

    if (tiktok) payload.tiktok = tiktok;

    const apt = cleanObject(raw.apt);

    if (apt) payload.apt = apt;

    const appsflyer = cleanObject(raw.appsflyer);

    if (appsflyer) payload.appsflyer = appsflyer;

    const firebase = cleanObject(raw.firebase);

    if (firebase) payload.firebase = firebase;

    return payload;
  };

  const buildEnterSitePayload = (): UserJourneyPayload =>
    buildPayload({
      user: userId.value ? { id: String(userId.value) } : undefined,
      device: fingerprint.value
        ? { fingerprint: fingerprint.value }
        : undefined,
      utm: marketingUtm.value,
      google: marketingGoogle.value,
      facebook: marketingFacebook.value,
      tiktok: marketingTiktok.value,
      apt: marketingApt.value,
      appsflyer: journeyStore.appsflyer,
      firebase: journeyStore.firebase,
    });

  const getStoredPayload = (): UserJourneyPayload =>
    buildPayload({
      user: journeyStore.user.id ? { id: journeyStore.user.id } : undefined,
      device: journeyStore.device.fingerprint
        ? { fingerprint: journeyStore.device.fingerprint }
        : undefined,
      utm: journeyStore.utm,
      google: journeyStore.google,
      facebook: journeyStore.facebook,
      tiktok: journeyStore.tiktok,
      apt: journeyStore.apt,
      appsflyer: journeyStore.appsflyer,
      firebase: journeyStore.firebase,
    });

  // O `user.id` do journeyStore tem um único escritor: o `enter_site`. Quem se
  // cadastra nunca dispara esse evento (as telas de registro chamam `setAuth`
  // direto), então o store fica anônimo até a próxima navegação de rota
  // acionar o middleware -- e todo evento que exige `user_id` tomava 422 nesse
  // meio. O usuário logado é o fallback.
  // Puro de propósito: o `persistPayload` grava o payload no store (e no
  // localStorage), então um helper que mutasse o argumento corromperia o
  // store no dia em que alguém o aplicasse ao `enterSiteEvent`.
  const withLoggedUser = (payload: UserJourneyPayload): UserJourneyPayload =>
    payload.user?.id || !userId.value
      ? payload
      : { ...payload, user: { id: String(userId.value) } };

  const persistPayload = (payload: UserJourneyPayload) => {
    journeyStore.setUser(payload.user ?? {});
    journeyStore.setDevice(payload.device ?? {});
    journeyStore.setUtm(payload.utm ?? {});
    journeyStore.setGoogle(payload.google ?? {});
    journeyStore.setFacebook(payload.facebook ?? {});
    journeyStore.setTiktok(payload.tiktok ?? {});
    journeyStore.setApt(payload.apt ?? {});
    journeyStore.setAppsflyer(payload.appsflyer ?? {});
    // NAO tocar em `firebase` aqui: o slot e escrito SO pela bridge
    // (setFirebaseIdentity no handler) e ja e persistido pelo pinia. Um
    // `?? {}` aqui apagaria uma identidade que chegou durante o round-trip do
    // sendEvent (enter_site + webPageReady acontecem juntos no boot).
    journeyStore.setAcquiredAt(marketingStore.acquiredAt);
  };

  const sendEvent = async (
    event: UserJourneyEvent,
    payload: UserJourneyPayload,
  ): Promise<boolean> => {
    // Bots não enviam user journey (melhora performance em testes)
    if (isBot.value) return false;

    try {
      await registerUserJourneyEventHandler({ event, ...payload });

      return true;
    } catch (error) {
      console.error('[user-journey] failed to send event', event, error);

      return false;
    }
  };

  const enterSiteEvent = async () => {
    const payload = buildEnterSitePayload();

    // No app, o /collector envia enter_site sem terceiros/fingerprint; o backend
    // só aceita com user_id/utm/apt. Sem nenhum deles, daria 422 — então nem
    // enviamos (ENG-397 / D5). Fora do app, comportamento inalterado.
    if (isNativeApp && !payload.user?.id && !payload.utm && !payload.apt) {
      return;
    }

    if (isDeepEqual(payload, getStoredPayload())) return;

    const sent = await sendEvent(UserJourneyEvent.ENTER_SITE, payload);

    if (sent) persistPayload(payload);
  };

  // Sem guarda de `user_id` por herança, não por decisão: o core ainda exige
  // o campo no `register_user`, então um payload anônimo aqui ainda toma 422.
  // Os call sites chamam depois de `await setAuth(...)`, que resolve o perfil.
  const registerUserEvent = () => {
    queueMicrotask(() =>
      sendEvent(
        UserJourneyEvent.REGISTER_USER,
        withLoggedUser(getStoredPayload()),
      ),
    );
  };

  // Momento do SUBMIT do KYC (não da aprovação): a attribution viaja aqui e
  // o core confirma a aprovação depois via buffer (cobre aprovação manual e
  // fora de sessão — o antigo evento client-side só via a janela de polling).
  const submitKycEvent = () => {
    const payload = withLoggedUser(getStoredPayload());

    if (!payload.user?.id) return; // sem usuário identificado não há o que rastrear

    queueMicrotask(() => sendEvent(UserJourneyEvent.SUBMIT_KYC, payload));
  };

  const blogPostClickEvent = (slug: string) => {
    if (!slug) return;

    const { user, device } = getStoredPayload();

    queueMicrotask(() =>
      sendEvent(UserJourneyEvent.BLOG_POST_CLICK, {
        user,
        device,
        slug,
      }),
    );
  };

  const homeMenuItemClickEvent = (
    itemId: string,
    renderedPosition?: number,
  ) => {
    if (!itemId) return;

    const { user, device } = getStoredPayload();

    queueMicrotask(() =>
      sendEvent(UserJourneyEvent.HOME_MENU_ITEM_CLICK, {
        user,
        device,
        home_menu_item_id: itemId,
        ...(typeof renderedPosition === 'number' && renderedPosition >= 1
          ? { home_menu_item_rendered_position: renderedPosition }
          : {}),
      }),
    );
  };

  const makeDepositEvent = (transactionId: string) => {
    const depositId = Number(transactionId?.match(/\d+/)?.[0]);

    if (Number.isNaN(depositId)) return;

    // Sem guarda de `user_id`: o core resolve o jogador pelo `deposit_id` na
    // cactus, então mandar sem identificação é melhor que não mandar.
    queueMicrotask(() =>
      sendEvent(UserJourneyEvent.MAKE_DEPOSIT, {
        ...withLoggedUser(getStoredPayload()),
        deposit_id: depositId,
      }),
    );
  };

  // Momento do request do saque (após /new-withdraws OK). O core casa esse
  // `valor` por igualdade exata contra o saque na cactus e confirma via buffer
  // (withdraw/first_withdraw). Mesmo valor mandado ao /new-withdraws → match.
  const withdrawEvent = (amount: number) => {
    if (!Number.isFinite(amount) || amount <= 0) return;

    const payload = withLoggedUser(getStoredPayload());

    if (!payload.user?.id) return; // core exige user_id no withdraw

    queueMicrotask(() =>
      sendEvent(UserJourneyEvent.WITHDRAW, { ...payload, valor: amount }),
    );
  };

  return {
    enterSiteEvent,
    registerUserEvent,
    submitKycEvent,
    makeDepositEvent,
    withdrawEvent,
    blogPostClickEvent,
    homeMenuItemClickEvent,
  };
};
